import { fmtCurrency } from "@/lib/format";
import { Panel, SectionLabel } from "@/components/common/ui-kit";
import { Delta, periodLabel } from "@/components/common/PeriodFilter";
import { cn } from "@/lib/utils";

const Metric = ({ label, value, tone, testId }) => (
  <div className="flex items-baseline justify-between gap-3 py-2.5" data-testid={testId}>
    <span className="text-sm text-slate-500">{label}</span>
    <span className={cn("font-display text-xl font-bold tracking-tight", tone || "text-ink")}>{value}</span>
  </div>
);

const FlowMetric = ({ label, k, period, money, testId }) => {
  const cur = period.current[k];
  if (cur === undefined) return null;
  return (
    <div className="flex items-baseline justify-between gap-3 py-2.5" data-testid={testId}>
      <span className="text-sm text-slate-500">{label}</span>
      <span className="text-right">
        <span className="block font-display text-xl font-bold tracking-tight text-ink" data-testid={`${testId}-value`}>{money === "pct" ? `${cur.toLocaleString("it-IT")}%` : money ? fmtCurrency(cur) : cur}</span>
        {money === "pct" && <span className="block text-[11px] text-slate-400">sull'imponibile</span>}
        {money === true && <span className="block text-[11px] text-slate-400" data-testid={`${testId}-net`}>imponibile</span>}
        <Delta cur={cur} prev={period.previous[k]} period={period.key} testId={`${testId}-delta`} />
      </span>
    </div>
  );
};

const PeriodBlock = ({ period, metrics, testId }) => {
  const rows = metrics.filter(([, k]) => period?.current[k] !== undefined);
  if (!rows.length) return null;
  return (
    <div className="mt-3 rounded-control border border-slate-100 bg-slate-50/60 px-3" data-testid={testId}>
      <p className="pt-3 text-[11px] font-semibold uppercase tracking-wide text-slate-400">{periodLabel(period.key)}</p>
      <div className="divide-y divide-slate-100">
        {rows.map(([label, k, money]) => <FlowMetric key={k} label={label} k={k} period={period} money={money} testId={`${testId}-${k.replace(/_/g, "-")}`} />)}
      </div>
    </div>
  );
};

export const CrmCard = ({ data, period }) => (
  <Panel className="animate-rise delay-1 p-5" data-testid="dashboard-section-crm">
    <SectionLabel>Clienti e lead</SectionLabel>
    <div className="mt-2 divide-y divide-slate-100">
      <Metric label="Clienti e prospect" value={data.companies} testId="crm-companies" />
      <Metric label="Da ricontattare (entro oggi)" value={data.to_recontact} tone={data.to_recontact ? "text-red-600" : undefined} testId="crm-to-recontact" />
      <Metric label="Senza prossima azione" value={data.no_next_action} tone={data.no_next_action ? "text-amber-700" : undefined} testId="crm-no-next-action" />
      {data.open_leads !== undefined && <Metric label="Lead aperti" value={data.open_leads} testId="crm-open-leads" />}
    </div>
    <PeriodBlock period={period} testId="crm-period" metrics={[["Nuovi lead", "new_leads"], ["Lead convertiti", "converted_leads"], ["Nuove aziende", "new_companies"]]} />
  </Panel>
);

export const CommercialCard = ({ data, period }) => (
  <Panel className="animate-rise delay-2 p-5" data-testid="dashboard-section-commerciale">
    <SectionLabel>Commerciale</SectionLabel>
    {data.pipeline_value !== undefined && (
      <div className="mt-3 rounded-control bg-slate-50 p-4">
        <p className="text-xs text-slate-500">Valore pipeline (ad oggi) · imponibile</p>
        <p className="mt-1 font-display text-3xl font-bold tracking-tight text-ink" data-testid="commercial-pipeline-value">{fmtCurrency(data.pipeline_value)}</p>
        <p className="mt-1 text-xs text-slate-500">Ponderato (imponibile): <span className="font-medium text-slate-700">{fmtCurrency(data.weighted_value)}</span></p>
      </div>
    )}
    <div className="mt-2 divide-y divide-slate-100">
      {data.open_opportunities !== undefined && <Metric label="Opportunità aperte" value={data.open_opportunities} testId="commercial-open-opportunities" />}
      {data.overdue_followups !== undefined && <Metric label="Follow-up scaduti" value={data.overdue_followups} tone={data.overdue_followups ? "text-red-600" : undefined} testId="commercial-overdue-followups" />}
      {data.open_quotes !== undefined && <Metric label="Offerte aperte (bozza/inviate)" value={data.open_quotes} tone={data.pending_quotes ? "text-amber-700" : undefined} testId="commercial-open-quotes" />}
    </div>
    <PeriodBlock period={period} testId="commercial-period" metrics={[
      ["Vinto", "won_value", true], ["Opportunità vinte", "won_count"], ["Chiusure previste", "closing"],
      ["Offerte inviate", "quotes_sent"], ["Offerte accettate", "quotes_accepted"], ["Valore accettato", "accepted_value", true],
      ["Ordini confermati", "orders_confirmed"], ["Valore ordini confermati", "orders_value", true], ["Margine ordini", "orders_margin", true], ["Margine ordini %", "orders_margin_pct", "pct"]]} />
    {period?.current.orders_no_cost > 0 && <p className="mt-2 text-xs text-amber-700" data-testid="commercial-orders-no-cost">{period.current.orders_no_cost} ordini senza costi completi esclusi dal margine.</p>}
  </Panel>
);

export const OperationsCard = ({ data, period }) => (
  <Panel className="animate-rise delay-3 p-5" data-testid="dashboard-section-operativo">
    <SectionLabel>Operativo</SectionLabel>
    <div className="mt-2 divide-y divide-slate-100">
      <Metric label="Interventi aperti" value={data.open_interventions} testId="operations-open-interventions" />
      <Metric label="Interventi programmati" value={data.scheduled_interventions} testId="operations-scheduled-interventions" />
      <Metric label="Rapportini da completare" value={data.reports_to_complete} tone={data.reports_to_complete ? "text-amber-700" : undefined} testId="operations-reports-to-complete" />
    </div>
    <PeriodBlock period={period} testId="operations-period" metrics={[["Interventi completati", "completed_interventions"], ["Rapportini confermati", "confirmed_reports"]]} />
  </Panel>
);
